import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

/** One stop on the rail: what it is called, how many are in it, its colour. */
export interface MilestoneStop {
  /** The value passed back on selection - a stage code, in both callers. */
  id: string;
  label: string;
  count: number;
  /** The stage's own colour. Falls back to the surrounding text colour. */
  color?: string;
}

export interface WorkflowMilestone {
  id: string;
  label: string;
  complete: boolean;
  disabled?: boolean;
}

/**
 * A pipeline drawn as a track: every stage, in order, with how many are in it.
 *
 * SHARED, because it was not. The Leads collection had this markup inline and
 * sixty lines of `ms-*` CSS in its own stylesheet, under a comment claiming
 * the CRM customer list used "the same control" - it did not; nothing else in
 * the app used those classes at all. So the second page that wanted a rail
 * (Roll-off Hires) had exactly two options: copy the markup and the CSS, or
 * lift them here. The repository's own rule about not growing a parallel file
 * tree per service line decides that.
 *
 * What a rail knows is the shape - dots, the line between them, which one is
 * chosen, how a count is drawn. What it deliberately does NOT know is where
 * the stages came from, what a count is counted over, or what choosing one
 * does. Those differ between the two collections and belong to them.
 *
 * In stage-filter mode, "All" is part of the control rather than a caller's extra button:
 * clearing the focus is the action both collection filters need, and leaving it to
 * callers is how two of them end up clearing it differently.
 */
@Component({
  selector: 'app-milestone-rail',
  standalone: true,
  host: { '[class.workflow]': 'workflow !== null' },
  imports: [CommonModule],
  templateUrl: './milestone-rail.component.html',
  styleUrls: ['./milestone-rail.component.css'],
})
export class MilestoneRailComponent {
  /** Null retains the existing stage-count filter. Progress is supplied by its owner. */
  @Input() workflow: readonly WorkflowMilestone[] | null = null;
  @Input() current: string | null = null;
  @Input() closed = false;
  @Input() label = 'Workflow steps';
  @Output() viewedChange = new EventEmitter<string>();

  view(id: string): void {
    if (this.workflow?.some(step => step.id === id && !step.disabled)) this.viewedChange.emit(id);
  }

  @Input() stops: readonly MilestoneStop[] = [];

  /** One focused stop, or several selected stops. Null/empty means All. */
  @Input() selected: string | readonly string[] | null = null;

  /** What "All" counts. Not summed from the stops: the collections that use
   *  this ask a server for a total, and a rail that added up its own dots
   *  would quietly disagree with the page whenever a stop was left out. */
  @Input() total = 0;

  @Input() allLabel = 'All';

  /**
   * Emits the stop to focus, or null to clear.
   *
   * With an array selection the caller toggles membership of the clicked id.
   * Clicking the single focused stop clears back to All. That is the behaviour the
   * Leads rail already had, and it is the reason the rail decides this rather
   * than the caller: a rail where the way out of a filter depends on which
   * page you are on is two controls wearing one appearance.
   */
  @Output() selectedChange = new EventEmitter<string | null>();

  choose(id: string | null): void {
    this.selectedChange.emit(typeof this.selected === 'string' && this.selected === id ? null : id);
  }

  isSelected(id: string | null): boolean {
    if (id === null) return this.selected === null || this.selected.length === 0;
    return typeof this.selected === 'string' ? this.selected === id : !!this.selected?.includes(id);
  }

  trackStop(_: number, stop: MilestoneStop): string {
    return stop.id;
  }
}
